/**
 * RIG mode, left footer: the two irreversible-ish steps of the mode — generate the skin weights,
 * then confirm the rig — plus the one line that says whether the weights still match the joints.
 *
 * They live in the footer and not in `rigPanel.ts` because the outliner above them fills the
 * column: at 1080p the old inline buttons sat below 858px of sections and were off-screen (audit
 * B). The whole staleness bookkeeping moved here with them, so there is exactly one owner.
 */

import type { CanonicalRigPlacement } from '../../shared';
import type { EditorContext } from '../context';
import type { Controllers } from '../controllers/types';
import { dialogs } from '../dialogs';
import { button, el, icon } from './dom';
import { createPanel, note, runAsync, type PanelHandle } from './panel';

export function mountRigFooterPanel(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  const { store } = ctx;
  /**
   * The placement the current weights were computed from. The store does not invalidate
   * `skinGenerated` when a joint moves, so staleness is tracked here by object identity.
   */
  let placementAtSkin: CanonicalRigPlacement | null = store.getState().rig.skinGenerated
    ? store.getState().rig.placement
    : null;
  let generating = false;

  /**
   * Regenerating recomputes every weight from the current joints. Per-joint corrections and
   * region overrides survive (they are parameters, not painted weights), but the solve is long
   * enough to ask first (wave-5 review finding 3).
   */
  function confirmRegenerate(): Promise<boolean> {
    if (!controllers.rig.hasSkinEdits()) return Promise.resolve(true);
    return dialogs.confirm({
      title: 'Regenerate skin weights?',
      message:
        'Regenerating recomputes the weights from the current joints; your per-joint corrections ' +
        'and region overrides are kept as parameters. Continue?',
      confirmLabel: 'Regenerate',
    });
  }

  function status(): string {
    const rig = store.getState().rig;
    if (!rig.skinGenerated) return 'weights: none';
    if (placementAtSkin && rig.placement !== placementAtSkin) return 'weights: stale (joints moved)';
    // The resolution only takes effect at the next Generate, so a changed one means stale weights.
    const stats = controllers.rig.skinStats();
    if (stats && stats.resolution !== rig.skinParams.resolution) {
      return 'weights: stale (resolution changed — regenerate)';
    }
    return 'weights: generated';
  }

  function generate(busy: boolean): void {
    if (busy) return;
    runAsync(confirmRegenerate(), (confirmed) => {
      if (!confirmed) return;
      generating = true;
      instance.schedule();
      runAsync(
        controllers.rig.generateSkin().finally(() => {
          generating = false;
          instance.schedule();
        }),
      );
    });
  }

  function render(root: HTMLElement): void {
    const rig = store.getState().rig;
    // `skinBusy` covers the solves this panel did not start (a live correction re-solve).
    const busy = generating || rig.skinBusy;

    root.appendChild(
      // Stacked, in the order they are meant to be used: Generate is the primary action until
      // there are weights, after which Confirm takes over as the way out of the mode.
      el('div', { class: 'buttons stack' }, [
        button(
          busy ? [icon('zap'), 'Generating…'] : [icon('zap'), 'Generate skin weights'],
          () => generate(busy),
          {
            class: rig.skinGenerated ? undefined : 'primary',
            disabled: !rig.placement || busy,
            'data-action': 'generate-skin',
          },
        ),
        button(
          [icon('check'), 'Confirm rig → Animate'],
          () =>
            runAsync(controllers.rig.confirmRig(), () => {
              store.actions.setMode('animate');
            }),
          {
            class: rig.skinGenerated ? 'primary' : undefined,
            'data-action': 'confirm-rig',
            disabled: !rig.skinGenerated,
            title: rig.skinGenerated ? 'Bind the generated rig' : 'Generate skin weights first',
          },
        ),
      ]),
    );

    root.appendChild(note(status(), 'note status'));
  }

  /* The slices `rigPanel` used to subscribe to for these controls, and nothing else. */
  const instance = createPanel(container, ctx, render, {
    className: 'rig-footer',
    slices: [
      (s) => s.rig.placement,
      (s) => s.rig.skinGenerated,
      (s) => s.rig.skinBusy,
      // A primitive, and it only changes from the resolution <select> — never during a drag.
      (s) => s.rig.skinParams.resolution,
    ],
  });

  instance.own(
    store.select(
      (s) => s.rig.skinGenerated,
      (generated) => {
        placementAtSkin = generated ? store.getState().rig.placement : null;
      },
    ),
  );

  return instance;
}
